// ContentSyndicationDashboard.js
// Coursera-branded, stacked (one-per-row) content-syndication dashboard
// ─ Preloaded CSV so it renders immediately
// ─ Uploaders for: Leads (required) + Intent (optional) + Account Enrichment (optional)
// ─ Filters, KPIs, Weekly Pacing, Top Assets (overrideable), Treemap + drilldowns,
//   Role heatmap, Target Account List segmentation
// Requires: recharts

import React, { useMemo, useRef, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
  Treemap,
  Rectangle,
} from "recharts";

/* ============================ Coursera palette ============================ */
const TOK = {
  blue500: "#2E89FF",
  blue700: "#2A73CC",
  blue900: "#1C3F7A",
  blue975: "#0E2B57",
  line: "#E6E7EB",
  ink: "#0B1A3C",
  muted: "#5C6570",
  tile: "#F8FAFE",
  white: "#FFFFFF",
};
const PALETTE = [
  "#2E89FF",
  "#6D8DD8",
  "#16335F",
  "#95B9FF",
  "#4C6FB6",
  "#1C3F7A",
  "#BFD3FF",
  "#2A73CC",
  "#23386A",
  "#A9C4FF",
];

/* ------------------------ Preloaded sample (exported) ------------------------ */
export const SAMPLE_CSV = `CreatedAt,Company,Domain,Industry,Job Title,asset_name,Campaign,ABM Segment,Status
2025-10-01,Adobe,adobe.com,Software,Chief of Staff - Engineering,AI Readiness Report,Q4 Syndication,Top 100,Valid
2025-10-02,Capital One,capitalone.com,Financial Services,Senior Director, Enterprise Data and ML,Generative AI Skills Brief,Q4 Syndication,Top 100,Valid
2025-10-02,T Rowe Price,troweprice.com,Financial Services,VP HR Digital Experiences,AI in HR Playbook,Q4 Syndication,Finance 200,Valid
`;

/* =============================== style shim =============================== */
function Styles() {
  return (
    <style>{`
    :root { --ink:#0B1A3C; --muted:#5C6570; --bg:#F8FAFE; --tile:#fff; --line:#E6E7EB; }
    .text-ink{color:var(--ink)} .text-muted{color:var(--muted)}
    .bg-bg{background:var(--bg)}
    .card{background:var(--tile); border:1px solid var(--line); border-radius:16px; padding:16px}
    .btn{background:${TOK.blue700}; color:#fff; border-radius:10px; padding:10px 14px; border:0; cursor:pointer}
    .btn.secondary{background:#eef2f7; color:#1d2b4d}
    .uploader,.uploader-inline{display:flex; gap:8px; align-items:center; border:1px dashed #c9d5ef; border-radius:12px; padding:10px}
    .uploader{flex-direction:column; align-items:flex-start}
    .label{font-weight:600}
    .hint{font-size:12px; color:#6b7280}
    .field{display:flex; flex-direction:column; gap:6px}
    .field-label{font-size:12px; color:#6b7280}
    .input{border:1px solid var(--line); border-radius:10px; padding:10px}
    .kpi{background:var(--tile); border:1px solid var(--line); border-radius:16px; padding:16px}
    .kpi-value{font-size:28px; font-weight:800}
    .kpi-label{font-size:12px; color:#6b7280}
    .ok{background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; border-radius:12px; padding:8px 10px}
    .controls{display:flex; gap:10px; align-items:center; margin-bottom:8px}
    .segment-select{min-width:160px}
    .treemap-legend{display:flex; gap:8px; flex-wrap:wrap; margin-top:8px}
    .badge{background:#f3f4f6; color:#111827; border-radius:999px; padding:4px 8px; font-size:12px}
    .heatgrid{display:grid; grid-template-columns:160px repeat(5, 1fr); gap:4px}
    .hcell{border:1px solid #D7E3FF; border-radius:8px; padding:8px; text-align:center}
    .hcell.head{background:#f3f6ff; font-weight:700}
    .hcell.key{background:#f9fafb; text-align:left}
    .hcell.val{color:#fff; font-weight:700}
    .hnote{font-size:12px; color:#6b7280; margin-top:6px}
    .tbl{width:100%; border-collapse:separate; border-spacing:0; font-size:14px}
    .tbl th,.tbl td{border-bottom:1px solid #eee; padding:8px 10px; text-align:left}
    .table-wrap{border:1px solid var(--line); border-radius:12px; overflow:hidden}
    .muted{color:#9ca3af}
  `}</style>
  );
}

/* =============================== small CSV utils =============================== */
function splitCSVLine(line) {
  const out = [];
  const re = /("([^"]*(?:""[^"]*)*)"|[^,]*)(,|$)/g;
  let m;
  while ((m = re.exec(line)) !== null) {
    const val = m[2] !== undefined ? m[2] : m[1];
    out.push((val ?? "").replace(/""/g, '"'));
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  return out;
}
function parseCSVStrict(text) {
  const lines = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  if (!lines.length) return { headers: [], data: [] };
  const headers = splitCSVLine(lines[0]).map((h) => (h ?? "").trim());
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const fields = splitCSVLine(line);
    const row = {};
    for (let c = 0; c < headers.length; c++) row[headers[c]] = fields[c] ?? "";
    data.push(row);
  }
  return { headers, data };
}
function headerMap(headers) {
  const map = new Map();
  headers.forEach((h) => map.set(h, h));
  headers.forEach((h) => map.set(h.toLowerCase(), h));
  return map;
}
function pick(row, H, names) {
  for (const n of names) {
    const k = H.get(n) || H.get(String(n).toLowerCase());
    const v = (k ? row[k] : row[n]) ?? "";
    if (v !== "") return v;
  }
  return "";
}

/* =============================== date helpers =============================== */
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function pad2(n) {
  n = parseInt(n, 10);
  return n < 10 ? `0${n}` : `${n}`;
}
function isoDate(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  const d = new Date(t);
  if (!isNaN(+d)) return d.toISOString().slice(0, 10);
  const m1 = t.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m1) {
    const [_, mm, dd, yy] = m1;
    const yyyy = yy.length === 2 ? `20${yy}` : yy;
    const dt = new Date(`${yyyy}-${pad2(mm)}-${pad2(dd)}`);
    if (!isNaN(+dt)) return dt.toISOString().slice(0, 10);
  }
  return "";
}
function weekStartISO(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(+d)) return "";
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday start
  const start = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
  start.setUTCDate(start.getUTCDate() + diff);
  return start.toISOString().slice(0, 10);
}
function addDaysISO(iso, days) {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function fmtShortDate(iso) {
  const d = new Date(iso);
  const M = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (isNaN(+d)) return iso;
  return `${M[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
function fmtWeekRange(isoStart) {
  const s = new Date(isoStart);
  if (isNaN(+s)) return "";
  const e = new Date(s);
  e.setUTCDate(e.getUTCDate() + 6);
  const M = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const sameMonth = s.getUTCMonth() === e.getUTCMonth();
  const left = `${M[s.getUTCMonth()]} ${s.getUTCDate()}`;
  const right = `${
    sameMonth ? "" : M[e.getUTCMonth()] + " "
  }${e.getUTCDate()}, ${e.getUTCFullYear()}`;
  return `${left}–${right}`;
}
function fmtMDY2(iso) {
  const d = new Date(iso);
  if (isNaN(+d)) return iso || "";
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${m}/${day}/${yy}`;
}

/* =============================== misc helpers =============================== */
function canonDomain(s) {
  const x = String(s || "")
    .trim()
    .toLowerCase();
  if (!x) return "";
  return x
    .replace(/^@/, "")
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
}
function domainToName(dom) {
  const d = String(dom || "")
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "");
  const label = d.split(".")[0] || "";
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : "";
}
function normalizeStatus(s) {
  const x = String(s || "").toLowerCase();
  if (x.includes("valid")) return "Valid";
  if (x.includes("invalid")) return "Invalid";
  if (x.includes("test")) return "Test";
  if (x.includes("caution") || x.includes("caut")) return "Caution";
  return "Other";
}
function toInt(val, fallback = 0) {
  if (val === null || val === undefined || val === "") return fallback;
  const n = parseInt(String(val).replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : fallback;
}
function toFloat(val) {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(String(val).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function pct(n, d) {
  if (!d) return 0;
  return Math.round((n / d) * 1000) / 10;
}
function eq(a, b) {
  return String(a || "").toLowerCase() === String(b || "").toLowerCase();
}
function sortedUnique(arr) {
  return Array.from(
    new Set(
      (arr || []).filter(
        (v) => v !== undefined && v !== null && String(v).trim() !== ""
      )
    )
  )
    .map(String)
    .sort((a, b) => a.localeCompare(b));
}
function intentBucket(score) {
  const s = toInt(score);
  if (s >= 70) return "High";
  if (s >= 30) return "Medium";
  if (s > 0) return "Low";
  return "None";
}
function arrayToCsv(matrix) {
  const BOM = "\uFEFF";
  const lines = matrix.map((row) => row.map(csvEsc).join(","));
  return BOM + lines.join("\r\n");
}
function csvEsc(val) {
  const s = String(val ?? "");
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}
function downloadText(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---- reducers + lookups ---- */
function groupBy(arr, keyFn) {
  const m = {};
  (arr || []).forEach((x) => {
    const k = keyFn(x);
    (m[k] = m[k] || []).push(x);
  });
  return m;
}
function countBy(arr, keyFn) {
  const m = {};
  (arr || []).forEach((x) => {
    const k = keyFn(x);
    m[k] = (m[k] || 0) + 1;
  });
  return m;
}
function indexBy(arr, key) {
  const o = {};
  (arr || []).forEach((x) => {
    const k = String(x?.[key] ?? "").trim();
    if (k) o[k] = x;
  });
  return o;
}
function indexByMulti(arr, keys) {
  const o = {};
  (arr || []).forEach((x) => {
    keys.forEach((key) => {
      const k = String(x?.[key] ?? "")
        .trim()
        .toLowerCase();
      if (k) o[k] = x;
    });
  });
  return o;
}

/* =============================== Normalizers =============================== */
// Robust lead normalizer: supports "Lead Delivery Time/Date/Lead Delivery" & asset_name
function normalizeLeadsCSV(text, sourceName = "") {
  const { headers, data } = parseCSVStrict(text);
  const H = headerMap(headers);
  const today = todayISO();

  return (data || [])
    .map((r) => {
      // prefer lead-delivery fields if present
      const createdRaw = pick(r, H, [
        "Lead Delivery Time",
        "Lead Delivery Date",
        "Lead Delivery",
        "CreatedAt",
        "Created",
        "Created Date",
        "Create Date",
        "Timestamp",
        "Date",
      ]);
      const createdAt = isoDate(createdRaw) || today;

      const email = (pick(r, H, ["Email", "email"]) || "").trim();

      const domain =
        canonDomain(
          pick(r, H, ["Domain", "EmailDomain", "Company Domain", "Website"])
        ) || (email.includes("@") ? email.split("@")[1] : "");

      const company =
        pick(r, H, ["Company", "Company Name", "Account", "Organization"]) ||
        (domain ? domainToName(domain) : "");

      // asset mapping: prefer asset_name
      let asset =
        pick(r, H, [
          "asset_name",
          "Asset",
          "Content Title",
          "Content",
          "Title",
          "Whitepaper",
          "Report",
        ]) || "";

      const status = normalizeStatus(
        pick(r, H, ["Status", "Lead Status", "QA Status", "Quality Status"]) ||
          "Valid"
      );

      return {
        sourceFile: sourceName,
        createdAt,
        week: weekStartISO(createdAt),
        company,
        account: company,
        domain,
        industry: pick(r, H, ["Industry"]) || "",
        jobTitle: pick(r, H, ["Job Title", "Title", "Job"]) || "",
        asset,
        campaign: pick(r, H, ["Campaign", "Campaign Name"]) || "",
        status,
        segment:
          pick(r, H, ["ABM Segment", "Segment", "Target Account List"]) || "",
        intentTopics: [],
        intentScore: 0,
      };
    })
    .filter((x) => x.company || x.domain);
}

function parseListish(v) {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  const s = String(v ?? "").trim();
  if (!s) return [];
  try {
    const j = JSON.parse(s);
    if (Array.isArray(j)) return j.map((x) => String(x).trim()).filter(Boolean);
  } catch {}
  const inner = s.replace(/^\s*\[|\]\s*$/g, "");
  const parts = inner.match(/'[^']*'|"[^"]*"|[^,|]+/g) || [];
  return parts.map((p) => p.replace(/^['"]|['"]$/g, "").trim()).filter(Boolean);
}

function normalizeIntentCSV(text) {
  const { headers, data } = parseCSVStrict(text);
  const H = headerMap(headers);
  return (data || []).map((r) => ({
    domain:
      canonDomain(pick(r, H, ["domain", "Domain", "EmailDomain", "Website"])) ||
      "",
    company: pick(r, H, ["Company", "Account"]) || "",
    intentTopics:
      parseListish(pick(r, H, ["Topics", "IntentTopics", "Signals"])) || [],
    intentScore: toInt(pick(r, H, ["Score", "IntentScore"])) || 0,
    website: pick(r, H, ["Website"]) || "",
  }));
}
function normalizeAccountCSV(text) {
  const { headers, data } = parseCSVStrict(text);
  const H = headerMap(headers);
  return (data || []).map((r) => ({
    domain: canonDomain(pick(r, H, ["Domain", "Website", "EmailDomain"])) || "",
    company: pick(r, H, ["Company", "Account"]) || "",
    vertical: pick(r, H, ["Vertical"]) || "",
    segment: pick(r, H, ["Segment"]) || "",
    region: pick(r, H, ["Region", "Country"]) || "",
    tier: pick(r, H, ["Tier", "GrowthTier"]) || "",
    website: pick(r, H, ["Website"]) || "",
  }));
}

/* =============================== KPIs / export =============================== */
function buildKPIs(rows) {
  const leads = rows.length;
  const campaigns = Math.max(
    1,
    new Set(rows.map((x) => x.campaign).filter(Boolean)).size
  );
  const companies = new Set(rows.map((x) => x.company).filter(Boolean)).size;
  const avgIntent =
    rows.length === 0
      ? 0
      : Math.round(
          (rows.reduce((n, r) => n + (toInt(r.intentScore) || 0), 0) /
            rows.length) *
            10
        ) / 10;
  return { leads, campaigns, companies, avgIntent };
}

function serialize(v) {
  return Array.isArray(v) ? v.join("|") : v ?? "";
}
function exportCSV(rows, includeIntent = false) {
  const header = [
    "Title",
    "Segment",
    "Date",
    "Company",
    "Domain",
    "Industry",
    "Asset",
    "Campaign",
  ];
  if (includeIntent) header.push("Intent Score", "Intent Topics");
  const matrix = [
    header,
    ...rows.map((x) => {
      const base = [
        x.jobTitle || "",
        x.segment || "",
        fmtMDY2(x.createdAt) || "",
        x.company || "",
        x.domain || "",
        x.industry || "",
        x.asset || "",
        x.campaign || "",
      ];
      if (includeIntent)
        base.push(toInt(x.intentScore) || "", (x.intentTopics || []).join("|"));
      return base.map(serialize);
    }),
  ];
  const csv = arrayToCsv(matrix);
  downloadText(csv, `export_${todayISO()}.csv`);
}
function exportThisWeekCSV(rows, includeIntent = false) {
  const startISO = weekStartISO(todayISO());
  const endISO = addDaysISO(startISO, 7);
  const thisWeek = rows.filter(
    (x) => x.createdAt >= startISO && x.createdAt < endISO
  );
  const header = ["Title", "Segment", "Date", "Company", "Domain", "Industry"];
  if (includeIntent) header.push("Intent Score", "Intent Topics");
  const matrix = [
    header,
    ...thisWeek.map((x) => {
      const base = [
        x.jobTitle || "",
        x.segment || "",
        fmtMDY2(x.createdAt) || "",
        x.company || "",
        x.domain || "",
        x.industry || "",
      ];
      if (includeIntent)
        base.push(toInt(x.intentScore) || "", (x.intentTopics || []).join("|"));
      return base.map(serialize);
    }),
  ];
  const csv = arrayToCsv(matrix);
  downloadText(csv, `new_leads_week_of_${startISO}.csv`);
}

/* =============================== Role Matrix =============================== */
const FN_ORDER = [
  "Engineering",
  "Data",
  "IT/Infra",
  "Security",
  "Product",
  "Marketing",
  "Sales",
  "Finance",
  "HR",
  "Legal",
  "Operations",
  "Other",
];
const SR_ORDER = ["IC", "Manager", "Director", "VP", "C-Level"];

function normFunction(r) {
  const s = `${r.function || ""} ${r.jobTitle || ""}`.toLowerCase();
  if (/data|ml|ai|analytics|scient/i.test(s)) return "Data";
  if (/sec|ciso|zero|iam|infosec/i.test(s)) return "Security";
  if (/eng|developer|dev|sre|platform/i.test(s)) return "Engineering";
  if (/it|infrastructure|sysadmin|help ?desk|network/i.test(s))
    return "IT/Infra";
  if (/product|pm\b|ux|design/i.test(s)) return "Product";
  if (/market|brand|demand|growth|content/i.test(s)) return "Marketing";
  if (/sales|sdr|ae|account exec|bizdev/i.test(s)) return "Sales";
  if (/finance|cfo|fp&a|accounting|tax/i.test(s)) return "Finance";
  if (/hr|people|talent|recruit/i.test(s)) return "HR";
  if (/legal|counsel|compliance/i.test(s)) return "Legal";
  if (/ops|operation|supply|logistics/i.test(s)) return "Operations";
  return "Other";
}
function normSeniority(r) {
  const t = `${r.position || ""} ${r.jobTitle || ""}`.toLowerCase();
  if (/chief|c[- ]?level|cxo|cto|ciso|cio|ceo|cfo|svp|executive/i.test(t))
    return "C-Level";
  if (/\bvp\b|vice president/i.test(t)) return "VP";
  if (/director|head of/i.test(t)) return "Director";
  if (/manager|lead/i.test(t)) return "Manager";
  return "IC";
}
function buildFuncSeniorMatrix(rows) {
  const funcs = new Set(),
    matrix = {};
  (rows || []).forEach((r) => {
    const fn = normFunction(r);
    const sr = normSeniority(r);
    funcs.add(fn);
    matrix[fn] = matrix[fn] || {};
    matrix[fn][sr] = (matrix[fn][sr] || 0) + 1;
  });

  const ordered = FN_ORDER.filter((f) => funcs.has(f));
  const SEN = SR_ORDER;

  const data = ordered.map((fn) => {
    const row = { fn, total: 0 };
    SEN.forEach((s) => {
      row[s] = matrix[fn]?.[s] || 0;
      row.total += row[s];
    });
    return row;
  });

  const max = data.reduce((m, r) => Math.max(m, ...SEN.map((k) => r[k])), 0);
  return { data, max, functions: ordered };
}

/* ---- Optional: parse an override CSV for the Assets chart (Asset, Count) ---- */
function parseAssetsCSV(text) {
  const { headers, data } = parseCSVStrict(text);
  const H = headerMap(headers);
  return (data || []).map((r) => ({
    asset:
      r[H.get("asset_name")] ||
      r[H.get("Asset")] ||
      r[H.get("asset")] ||
      r[H.get("Content")] ||
      r[H.get("Title")] ||
      "",
    count:
      toInt(r[H.get("Count")] || r[H.get("Leads")] || r[H.get("#")] || "0") ||
      0,
  }));
}

/* ================================ Components ================================ */
function Uploader({ label, hint, onFile, accept = ".csv,text/csv" }) {
  return (
    <label className="uploader">
      <div className="label">{label}</div>
      <input
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const r = new FileReader();
          r.onload = (ev) => onFile(f.name, String(ev.target?.result || ""));
          r.readAsText(f);
        }}
      />
      {hint ? <div className="hint">{hint}</div> : null}
    </label>
  );
}
function UploaderInline({ label, onFile, accept = ".csv,text/csv" }) {
  return (
    <label className="uploader-inline">
      <span>{label}</span>
      <input
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const r = new FileReader();
          r.onload = (ev) => onFile(f.name, String(ev.target?.result || ""));
          r.readAsText(f);
        }}
      />
    </label>
  );
}
function Input({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}
function Select({ label, value, onChange, options = [] }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">(All)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
function KPI({ label, value }) {
  return (
    <div className="kpi">
      <div className="kpi-value">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="card">
      <h3 className="mb-2">{title}</h3>
      {children}
    </div>
  );
}

/* -------- Treemap content + tooltip (labels forced white for legibility) -------- */
function NodeRect(props) {
  const { x, y, width, height, name, index, size } = props;
  const fill = PALETTE[index % PALETTE.length];
  return (
    <g>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} />
      {width > 60 && height > 24 && (
        <text
          x={x + 6}
          y={y + 18}
          fill={TOK.white}
          fontSize={12}
          style={{
            paintOrder: "stroke",
            stroke: "rgba(0,0,0,.35)",
            strokeWidth: 1,
          }}
        >
          {name} ({size})
        </text>
      )}
    </g>
  );
}
function TreemapTooltip({ active, payload, label, total }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  const share = total ? `${pct(p.size, total)}%` : "";
  return (
    <div className="tt">
      <div className="tt-title">{p.name}</div>
      <div className="tt-sub">
        {label || "Leads"}: {p.size} {total ? `• ${share}` : ""}
      </div>
    </div>
  );
}

/* -------------------------- Heatmap grid component ------------------------- */
function HeatmapGrid({ rows, max }) {
  const scale = (v) => (!max ? 0 : 0.15 + 0.85 * (v / max));
  return (
    <>
      <div className="heatgrid">
        <div className="hcell head"></div>
        {SR_ORDER.map((s) => (
          <div key={s} className="hcell head">
            {s}
          </div>
        ))}
        {rows.map((r) => (
          <React.Fragment key={r.fn}>
            <div className="hcell key">{r.fn}</div>
            {SR_ORDER.map((s) => (
              <div
                key={s}
                className="hcell val"
                style={{
                  background: `rgba(46,137,255,${scale(r[s])})`,
                  borderColor: "#D7E3FF",
                  color: "#fff",
                  fontWeight: 700,
                }}
              >
                {r[s]}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="hnote">
        Color intensity = count by Function × Seniority.
      </div>
    </>
  );
}

/* --------------- Target Account List Segmentation component --------------- */
/* --------------- Target Account List Segmentation component --------------- */
function Segmentation({ filtered }) {
  const [showOther, setShowOther] = React.useState(false);

  const { segTop10, segOthers, segOtherTotal, segChart } = React.useMemo(() => {
    const segCounts = countBy(
      filtered,
      (x) =>
        (
          x.segment ||
          x["ABM Segment"] ||
          x["Target Account List"] ||
          "(unspecified)"
        )
          .toString()
          .trim() || "(unspecified)"
    );

    const rows = Object.entries(segCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const top10 = rows.slice(0, 10);
    const others = rows.slice(10);
    const otherTotal = others.reduce((n, r) => n + r.count, 0);

    return {
      segTop10: top10,
      segOthers: others,
      segOtherTotal: otherTotal,
      segChart: [
        ...top10,
        ...(otherTotal ? [{ name: "Other", count: otherTotal }] : []),
      ],
    };
  }, [filtered]);

  // autosize Y axis, but keep plot pulled left
  const yWidth = React.useMemo(() => {
    const longest = Math.max(...segChart.map((d) => (d.name || "").length), 8);
    return Math.min(200, Math.max(110, longest * 7));
  }, [segChart]);

  if (segTop10.length === 0 && segOtherTotal === 0) {
    return <div className="text-muted text-sm">No segment data yet.</div>;
  }

  return (
    <>
      {/* explicit height so the chart shows without Tailwind */}
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={segChart}
            margin={{ left: 16, right: 16, top: 4, bottom: 4 }}
            barSize={30}
            barCategoryGap={20}
          >
            <CartesianGrid stroke="#eef2f6" strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={yWidth}
              tick={{ fontSize: 14 }}
            />
            <RTooltip />
            <Bar
              dataKey="count"
              name="# Leads"
              onClick={(_, idx) => {
                const d = segChart[idx];
                if (d?.name === "Other" && segOtherTotal > 0) {
                  setShowOther((v) => !v);
                }
              }}
            >
              {segChart.map((_, i) => (
                <Cell key={i} fill={PALETTE[(i + 6) % PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showOther && segOthers.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 10 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Other segments</th>
                <th style={{ width: 120 }}># Leads</th>
              </tr>
            </thead>
            <tbody>
              {segOthers.map((r, i) => (
                <tr key={r.name + i}>
                  <td>{r.name}</td>
                  <td>{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* =============================== Top component ============================== */
export default function ContentSyndicationDashboard() {
  /* ----------------------------- Upload / data state ---------------------------- */
  const [leadRows, setLeadRows] = useState(() =>
    normalizeLeadsCSV(SAMPLE_CSV, "sample.csv")
  );
  const [intentRows, setIntentRows] = useState([]); // optional
  const [accountRows, setAccountRows] = useState([]); // optional
  const [filenameLead, setFilenameLead] = useState("sample.csv");

  // Asset override (optional)
  const [assetOverride, setAssetOverride] = useState([]); // [{asset, count}]

  // Manual lead goal (for pie gauge)
  const [leadGoal, setLeadGoal] = useState(250);

  // UI hides
  const [showOptionalUploaders, setShowOptionalUploaders] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  /* ------------------------------- UI filters ------------------------------ */
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [intentPriority, setIntentPriority] = useState("");
  const [industry, setIndustry] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [asset, setAsset] = useState("");
  const [status, setStatus] = useState("");
  const [domainQuery, setDomainQuery] = useState("");

  /* -------------------------- Normalize + join data ------------------------- */
  const leads = useMemo(() => {
    if (!leadRows.length) return [];
    const intentByDomain = indexByMulti(intentRows, [
      "domain",
      "emailDomain",
      "website",
    ]);
    const intentByCompany = indexBy(intentRows, "company");
    const acctByDomain = indexByMulti(accountRows, ["domain", "website"]);
    const acctByCompany = indexBy(accountRows, "company");

    return leadRows.map((r) => {
      const dom = (r.domain || "").toLowerCase();
      const co = (r.account || r.company || "").trim();
      const iHit = intentByDomain[dom] || intentByCompany[co] || {};
      const aHit = acctByDomain[dom] || acctByCompany[co] || {};
      const createdRaw = r.createdAt || r.Date || r.Timestamp || "";

      return {
        createdAt: isoDate(createdRaw) || createdRaw || todayISO(),
        company: r.company || domainToName(r.domain),
        account: r.account || r.company || "",
        domain: canonDomain(r.domain || ""),
        industry: r.industry || "",
        jobTitle: r.jobTitle || "",
        asset: r.asset || "",
        campaign: r.campaign || "",
        status: normalizeStatus(r.status || "Valid"),
        intentTopics: parseListish(iHit.intentTopics) || [],
        intentScore: toInt(iHit.intentScore, 0),
        vertical: aHit.vertical || r.vertical || "",
        segment: r.segment || aHit.segment || "",
        region: aHit.region || r.region || "",
        tier: aHit.tier || r.tier || "",
      };
    });
  }, [leadRows, intentRows, accountRows]);

  /* --------------------------------- Filters -------------------------------- */
  const filtered = useMemo(() => {
    let list = leads;
    if (dateFrom)
      list = list.filter((x) => x.createdAt && x.createdAt >= dateFrom);
    if (dateTo) list = list.filter((x) => x.createdAt && x.createdAt <= dateTo);
    if (intentPriority)
      list = list.filter((x) => intentBucket(x.intentScore) === intentPriority);
    if (industry) list = list.filter((x) => eq(x.industry, industry));
    if (jobTitle) {
      const q = jobTitle.toLowerCase();
      list = list.filter((x) =>
        String(x.jobTitle || "")
          .toLowerCase()
          .includes(q)
      );
    }
    if (status) list = list.filter((x) => eq(x.status, status));
    if (asset) list = list.filter((x) => eq(x.asset, asset));
    if (domainQuery)
      list = list.filter((x) =>
        (x.domain || "").toLowerCase().includes(domainQuery.toLowerCase())
      );
    return list;
  }, [
    leads,
    dateFrom,
    dateTo,
    intentPriority,
    industry,
    jobTitle,
    status,
    asset,
    domainQuery,
  ]);

  /* ---------------------------------- KPIs ---------------------------------- */
  const kpis = useMemo(() => buildKPIs(filtered), [filtered]);

  /* ---------------------------- Weekly pacing ---------------------------- */
  const weekly = useMemo(() => {
    const valid = filtered.filter((r) => !!isoDate(r.createdAt));
    const by = groupBy(valid, (x) => weekStartISO(x.createdAt));
    const pts = Object.entries(by)
      .map(([startISO, arr]) => ({
        startISO,
        label: fmtShortDate(startISO),
        labelLong: `Week of ${fmtWeekRange(startISO)}`,
        newLeads: arr.length,
      }))
      .sort((a, b) => a.startISO.localeCompare(b.startISO));
    let running = 0;
    pts.forEach((p) => {
      running += p.newLeads;
      p.cumLeads = running;
    });
    return pts;
  }, [filtered]);

  /* ---------------------------- Assets (Top 15) --------------------------- */
  const assetsBar = useMemo(() => {
    if (assetOverride.length) {
      return assetOverride
        .map((r) => ({ asset: r.asset || "", count: toInt(r.count) }))
        .filter((r) => r.asset)
        .sort((a, b) => b.count - a.count)
        .slice(0, 15);
    }
    const by = countBy(filtered, (x) => x.asset || "(unknown)");
    return Object.entries(by)
      .map(([asset, count]) => ({ asset, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [filtered, assetOverride]);

  /* ===================== Treemap + Drill-downs ====================== */
  const [groupByMode, setGroupByMode] = useState("company"); // company | industry
  const [drillKey, setDrillKey] = useState("");
  const { tmapNodes, tmapTotal, seeLine, defaultKey } = useMemo(() => {
    const keyFn =
      groupByMode === "company"
        ? (x) => x.company || domainToName(x.domain) || "(Unknown)"
        : (x) => x.industry || "(Unknown)";
    const counts = countBy(filtered, keyFn);
    const nodes = Object.entries(counts)
      .map(([name, size]) => ({ name, size }))
      .sort((a, b) => b.size - a.size);
    const total = nodes.reduce((n, d) => n + d.size, 0);
    const t1 = nodes[0],
      t2 = nodes[1];
    const see =
      t1 && t2
        ? `${t1.name} + ${t2.name} = ${pct(
            t1.size + t2.size,
            total
          )}% of all leads`
        : t1
        ? `${t1.name} = ${pct(t1.size, total)}% of all leads`
        : "";
    return {
      tmapNodes: nodes,
      tmapTotal: total,
      seeLine: see,
      defaultKey: t1?.name || "",
    };
  }, [filtered, groupByMode]);
  const selectedKey = drillKey || defaultKey;
  const drillRows = useMemo(() => {
    if (!selectedKey) return [];
    if (groupByMode === "company") {
      return filtered.filter(
        (r) =>
          (r.company || domainToName(r.domain) || "(Unknown)") === selectedKey
      );
    }
    return filtered.filter((r) => (r.industry || "(Unknown)") === selectedKey);
  }, [filtered, groupByMode, selectedKey]);

  const { data: funcStack, max: heatMax } = useMemo(
    () => buildFuncSeniorMatrix(drillRows),
    [drillRows]
  );

  /* ----------------------------- Options for filters ---------------------------- */
  const optsIndustry = useMemo(
    () => sortedUnique(leads.map((x) => x.industry)),
    [leads]
  );
  const optsAssets = useMemo(
    () => sortedUnique(leads.map((x) => x.asset)),
    [leads]
  );
  const optsStatus = ["", "Valid", "Invalid", "Test", "Caution", "Other"];

  /* --------------------------------- Render --------------------------------- */
  const refWeekly = useRef(null);
  return (
    <div className="min-h-screen text-ink bg-bg">
      <Styles />

      <header className="px-6 py-4 text-center">
        <h1 className="text-2xl font-bold">
          Content Syndication — Coursera View
        </h1>
        <div className="text-muted text-sm">
          Preloaded example of our content-syndication reporting. Upload your
          full list to replace the sample; charts render one per row using
          Coursera’s palette.
        </div>
      </header>

      {/* Upload + primary actions */}
      <section className="card">
        <h3 className="mb-2">Upload CSVs</h3>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          <div className="card" style={{ padding: 12 }}>
            <Uploader
              label="Lead Report (required)"
              hint="Any column order. We’ll auto-map headers."
              onFile={(name, text) => {
                setFilenameLead(name);
                setLeadRows(normalizeLeadsCSV(text, name));
              }}
              accept=".csv,text/csv"
            />
          </div>

          <div className="text-right">
            <button
              className="btn secondary"
              onClick={() => setShowOptionalUploaders((v) => !v)}
            >
              {showOptionalUploaders
                ? "Hide optional uploaders"
                : "Show optional uploaders"}
            </button>
          </div>

          {showOptionalUploaders && (
            <>
              <Uploader
                label="Intent CSV (optional)"
                hint="domain/company, topics, score"
                onFile={(_, text) => setIntentRows(normalizeIntentCSV(text))}
                accept=".csv,text/csv"
              />
              <Uploader
                label="Account Enrichment (optional)"
                hint="domain/company → vertical/segment/region/tier"
                onFile={(_, text) => setAccountRows(normalizeAccountCSV(text))}
                accept=".csv,text/csv"
              />
            </>
          )}
        </div>

        {!!leadRows.length && (
          <div className="ok mt-3">
            Loaded {leadRows.length.toLocaleString()} rows from{" "}
            <b>{filenameLead}</b>.
          </div>
        )}

        <div
          style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}
        >
          <button
            className="btn"
            onClick={() => exportThisWeekCSV(filtered, !!intentRows.length)}
          >
            Export this week’s new
          </button>
          <button
            className="btn secondary"
            onClick={() => setShowMoreFilters((v) => !v)}
          >
            {showMoreFilters ? "Hide filters" : "More filters"}
          </button>
          <button
            className="btn secondary"
            onClick={() => setShowSecondary((v) => !v)}
          >
            {showSecondary ? "Hide secondary KPIs" : "Show secondary KPIs"}
          </button>
          <button
            className="btn secondary"
            onClick={() => exportCSV(filtered, !!intentRows.length)}
          >
            Export current slice
          </button>
        </div>
      </section>

      {showMoreFilters && (
        <section className="card" style={{ marginTop: 12 }}>
          <div className="grid md:grid-cols-4 gap-3">
            <Input
              label="From"
              type="date"
              value={dateFrom}
              onChange={setDateFrom}
            />
            <Input label="To" type="date" value={dateTo} onChange={setDateTo} />
            <Select
              label="Industry"
              value={industry}
              onChange={setIndustry}
              options={optsIndustry}
            />
            <Input
              label="Job Title"
              value={jobTitle}
              onChange={setJobTitle}
              placeholder='e.g., "Director of Engineering"'
            />
            <Select
              label="Asset"
              value={asset}
              onChange={setAsset}
              options={optsAssets}
            />
            <Select
              label="Status (power filter)"
              value={status}
              onChange={setStatus}
              options={optsStatus}
            />
            <Input
              label="Search domain"
              value={domainQuery}
              onChange={setDomainQuery}
              placeholder="e.g., salesforce.com"
            />
            {!!intentRows.length && (
              <Select
                label="Intent Prioritization"
                value={intentPriority}
                onChange={setIntentPriority}
                options={["High", "Medium", "Low", "None"]}
              />
            )}
          </div>
        </section>
      )}

      {/* KPIs row (primary + optional secondary) */}
      <section
        className="grid lg:grid-cols-4 gap-3 px-1"
        style={{ marginTop: 12 }}
      >
        <KPI label="Leads" value={kpis.leads} />
        <KPI label="Unique Companies" value={kpis.companies} />
        <KPI label="Campaigns (min 1)" value={kpis.campaigns} />
        {showSecondary && (
          <KPI label="Avg. Intent Score" value={kpis.avgIntent || "—"} />
        )}
      </section>

      {/* Row 1: Goal + Weekly */}
      <section className="grid lg:grid-cols-2 gap-3" style={{ marginTop: 12 }}>
        <Card title="Lead Goal Progress">
          <div className="flex items-center" style={{ gap: 16 }}>
            <div style={{ width: 260, height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "done",
                        value: Math.min(kpis.leads, Math.max(leadGoal, 0)),
                      },
                      {
                        name: "remain",
                        value: Math.max(leadGoal - kpis.leads, 0),
                      },
                    ]}
                    dataKey="value"
                    innerRadius={90}
                    outerRadius={110}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <Cell fill={TOK.blue700} />
                    <Cell fill="#e5e7eb" />
                  </Pie>
                  <RTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="kpi-value" style={{ marginBottom: 6 }}>
                {kpis.leads.toLocaleString()} /{" "}
                {leadGoal ? leadGoal.toLocaleString() : "—"}
              </div>
              <div className="text-muted text-sm" style={{ marginBottom: 10 }}>
                {leadGoal
                  ? `${pct(kpis.leads, leadGoal)}% of goal`
                  : "Set a goal to track progress"}
              </div>
              <Input
                label="Lead Goal"
                type="number"
                value={leadGoal}
                onChange={(v) => setLeadGoal(toInt(v))}
                placeholder="e.g., 250"
              />
            </div>
          </div>
        </Card>

        <Card title="Weekly Pacing">
          <div className="h-72" ref={refWeekly} style={{ height: 300 }}>
            {weekly.length === 0 ? (
              <div className="text-muted text-sm">No dated rows yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={weekly}>
                  <CartesianGrid stroke="#eef2f6" strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <RTooltip
                    labelFormatter={(label, payload) =>
                      payload?.[0]?.payload?.labelLong || label
                    }
                  />
                  <Legend />
                  <Bar dataKey="newLeads" name="New (weekly)">
                    {weekly.map((_, i) => (
                      <Cell key={i} fill={PALETTE[(i + 2) % PALETTE.length]} />
                    ))}
                  </Bar>
                  <Line
                    type="monotone"
                    dataKey="cumLeads"
                    name="Cumulative"
                    stroke={TOK.blue975}
                    dot={false}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </section>

      {/* Row 2 */}
      <section className="grid lg:grid-cols-2 gap-3" style={{ marginTop: 12 }}>
        <Card title="Assets (Top 15)">
          <div className="h-80" style={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={assetsBar}
                margin={{ left: 20, right: 24, top: 4, bottom: 4 }}
                barSize={28}
                barCategoryGap={20}
              >
                <CartesianGrid stroke="#eef2f6" strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="asset"
                  width={220} // was 280 — this frees horizontal space
                  tick={{ fontSize: 14 }}
                />
                <RTooltip />
                <Bar dataKey="count" name="# Leads">
                  {assetsBar.map((_, i) => (
                    <Cell key={i} fill={PALETTE[(i + 4) % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-muted text-sm" style={{ marginTop: 10 }}>
            Optional: upload a simple CSV to replace this chart. Columns like{" "}
            <code>asset_name</code>, <code>Asset</code>, and <code>Count</code>{" "}
            or <code>Leads</code>.
          </div>
          <UploaderInline
            label="Override Assets CSV"
            onFile={(_, text) => setAssetOverride(parseAssetsCSV(text))}
            accept=".csv,text/csv"
          />
        </Card>

        <Card title="Where We’re Winning — Treemap">
          <div className="controls">
            <span className="field-label">Group by</span>
            <select
              className="input segment-select"
              value={groupByMode}
              onChange={(e) => {
                setGroupByMode(e.target.value);
                setDrillKey("");
              }}
            >
              <option value="company">Company</option>
              <option value="industry">Industry</option>
            </select>
            {tmapNodes.length > 0 && (
              <>
                <span className="field-label">Focus</span>
                <select
                  className="input segment-select"
                  value={selectedKey}
                  onChange={(e) => setDrillKey(e.target.value)}
                >
                  {tmapNodes.map((n) => (
                    <option key={n.name} value={n.name}>
                      {n.name} ({n.size})
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
          {tmapNodes.length === 0 ? (
            <div className="text-muted text-sm">Not enough data yet.</div>
          ) : (
            <>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <Treemap
                    data={tmapNodes}
                    dataKey="size"
                    nameKey="name"
                    isAnimationActive={false}
                    content={<NodeRect />}
                    onClick={(n) => setDrillKey(n?.name || "")}
                  >
                    <RTooltip
                      content={
                        <TreemapTooltip total={tmapTotal} label="Leads" />
                      }
                    />
                  </Treemap>
                </ResponsiveContainer>
              </div>
              <div className="treemap-legend">
                {seeLine && (
                  <span className="badge">
                    <b>See:</b> {seeLine}
                  </span>
                )}
                {groupByMode === "company" && (
                  <span className="badge">
                    <b>Think:</b> Enterprise tech + finance dominate.
                  </span>
                )}
                <span className="badge">
                  <b>Wonder:</b> Which job titles are we actually reaching?
                </span>
              </div>
            </>
          )}
        </Card>

        <Card
          title={`Who We’re Reaching — ${
            selectedKey || (groupByMode === "company" ? "Company" : "Industry")
          }`}
        >
          {funcStack.length === 0 ? (
            <div className="text-muted text-sm">
              Select a segment above to view roles reached.
            </div>
          ) : (
            <div className="h-72" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funcStack} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid stroke="#eef2f6" strokeDasharray="3 3" />
                  <XAxis dataKey="fn" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Legend />
                  {SR_ORDER.map((sr, i) => (
                    <Bar
                      key={sr}
                      dataKey={sr}
                      name={sr}
                      stackId="a"
                      fill={PALETTE[(i + 2) % PALETTE.length]}
                    />
                  ))}
                  <RTooltip />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </section>

      {/* Target Account List Segmentation */}
      <Card
        title="Target Account List Segmentation (Top 10 + Other)"
        style={{ marginTop: 12 }}
      >
        <Segmentation filtered={filtered} />
      </Card>

      <footer className="text-center text-muted text-xs py-4">
        Built for high-signal content syndication analytics. Rev 2.
      </footer>
    </div>
  );
}
